import { describeProposalOperation } from "work/proposals/negotiation/proposal-change-descriptors";
import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { proposalFieldForTimelinePath } from "work/proposals/negotiation/proposal-field-timeline-path";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * Whether the next Save contains a user-visible content change for this exact field control.
 *
 * This is the field-level counterpart to `proposalPendingChangeCountsByCategory`, which powers the
 * blue pending-change chip in the tab. Both intentionally ignore operations the Save confirmation
 * cannot describe. Collection rows additionally match their stable item identity, so editing one fee
 * category or document does not turn every sibling's history icon blue.
 */
export function proposalFieldHasPendingChange(
  operations: readonly ProposalContentOperation[] | undefined,
  field: ProposalField | null | undefined,
): boolean {
  if (!operations?.length || !field) return false;

  return operations.some((operation) => {
    if (!describeProposalOperation(operation)) return false;

    const identity = field.timelineIdentity();
    if (identity.kind === "scalarWithId") return operation.path === identity.path;

    const operationField = proposalFieldForTimelinePath(operation.path);
    if (!operationField || operationField.name !== field.name) return false;
    if (identity.kind === "scalar") return operationField.isEqualTo(field);
    if (
      operation.kind !== "CollectionItemAdded" &&
      operation.kind !== "CollectionItemUpdated" &&
      operation.kind !== "CollectionItemRemoved"
    ) {
      return false;
    }

    return collectionOperationMatchesFieldId(operation, identity.itemId);
  });
}

function collectionOperationMatchesFieldId(
  operation: Exclude<ProposalContentOperation, { kind: "FieldSet" | "ResolutionDecision" }>,
  fieldId: string,
): boolean {
  const normalizedFieldId = fieldId.toLowerCase();
  const normalizedOperationId = operation.itemId.toLowerCase();
  if (
    normalizedOperationId === normalizedFieldId ||
    normalizedOperationId.startsWith(`${normalizedFieldId}:`)
  ) {
    return true;
  }

  if ("value" in operation && proposalCollectionItemHasIdentity(operation.value, fieldId)) return true;
  return "expectedValue" in operation && proposalCollectionItemHasIdentity(operation.expectedValue, fieldId);
}
